import { Autocomplete, CircularProgress, Grid, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import { getStainingMethodByStainType, StainingMethod } from 'interfaces/stainType';
import { find, first, get, map, size } from 'lodash';
import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { ChannelsController } from '../ChannelsController';
import { GenerateStudyReportFormValues } from './types';
import { changeStainTypeAndMarkers } from './utils';

export interface AdditionalParametersStepProps {
  methods: UseFormReturn<GenerateStudyReportFormValues, any>;
  checkValidationAndSetIsStepFailed: () => void;
  selectedStainType: string;
  setSelectedStainType: (stain: string) => void;
  casesParams: CasesParams;
}

export const AdditionalParametersStep: React.FC<AdditionalParametersStepProps> = ({
  methods,
  checkValidationAndSetIsStepFailed,
  selectedStainType,
  setSelectedStainType,
  casesParams,
}) => {
  const {
    watch,
    formState: { errors },
  } = methods;

  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();
  const stainingMethod = getStainingMethodByStainType(selectedStainType);

  const { data: stains } = useQuery({
    queryKey: ['slidesStainsTypes', casesParams],
    queryFn: ({ signal }) => getStainTypeFilteredIds(casesParams, signal),
  });

  const stainOptions = map(stains, (stain) => {
    return {
      text: stainTypeIdToDisplayName(stain),
      value: stain,
    };
  });

  const onChangeStainType = (stain: string) => {
    changeStainTypeAndMarkers(
      stain || '',
      selectedStainType,
      methods.getValues('configParams.markers') || [],
      setSelectedStainType,
      (markers) => methods.setValue('configParams.markers', markers)
    );
  };

  useEffect(() => {
    if (size(stainOptions) === 1 && !selectedStainType) {
      onChangeStainType(first(stainOptions).value);
    }
  }, [JSON.stringify(stainOptions)]);

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Autocomplete
          disabled={selectedStainType && size(stainOptions) === 1}
          options={stainOptions}
          getOptionLabel={(option) => option.text}
          value={
            find(stainOptions, (opt) => opt.value === selectedStainType) || {
              text: selectedStainType,
              value: selectedStainType,
            }
          }
          onChange={(event, newValue) => {
            onChangeStainType(newValue?.value);
          }}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          loading={isLoadingStainTypeOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Stain"
              placeholder="Select Stain"
              required
              disabled={isLoadingStainTypeOptions}
              onClick={(event) => {
                event.stopPropagation();
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoadingStainTypeOptions ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </Grid>

      <Grid item>
        {stainingMethod === StainingMethod.mplex && (
          <ChannelsController
            name="configParams.markers"
            error={Boolean(get(errors, 'configParams.markers'))}
            helperText={
              !selectedStainType
                ? 'Please select a stain to see available channels'
                : get(errors, 'configParams.markers')?.message
            }
            methods={methods}
            additionalOnChange={() => {
              checkValidationAndSetIsStepFailed();
            }}
            disabled={!selectedStainType}
            casesParams={casesParams}
          ></ChannelsController>
        )}
      </Grid>
    </Grid>
  );
};
